"use client";

import { useEffect, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LogOut } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import type { UserSession } from "@/types/user";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useTranslation } from "react-i18next";

export function SessionSettings() {
  const { t } = useTranslation();
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [sessions, setSessions] = useState<UserSession[]>([]);
  const [isRevokingSession, setIsRevokingSession] = useState<string | null>(
    null,
  );
  const [isRevokingOtherSessions, setIsRevokingOtherSessions] = useState(false);

  const {
    data: sessionData,
    refetch: refetchSessions,
    error: sessionsError,
    status: sessionsStatus,
  } = trpc.settings.listSessions.useQuery();

  useEffect(() => {
    if (sessionsStatus === "pending") {
      setIsLoadingSessions(true);
    } else if (sessionsStatus === "error") {
      toast.error(sessionsError?.message || t('sessionSettings.loadErrorToast'));
      setSessions([]);
      setIsLoadingSessions(false);
    } else if (sessionsStatus === "success") {
      setSessions((sessionData as UserSession[] | undefined) || []);
      setIsLoadingSessions(false);
    }
  }, [sessionsStatus, sessionData, sessionsError]);

  const { mutate: revokeSessionMutation } =
    trpc.settings.revokeSession.useMutation({
      onSuccess: () => {
        toast.success(t('sessionSettings.revokeSuccessToast'));
        refetchSessions();
      },
      onError: (error) => {
        toast.error(error.message || t('sessionSettings.revokeErrorToast'));
      },
      onSettled: () => {
        setIsRevokingSession(null);
      },
    });

  const { mutate: revokeOtherSessionsMutation } =
    trpc.settings.revokeOtherSessions.useMutation({
      onSuccess: () => {
        toast.success(t('sessionSettings.revokeOtherSuccessToast'));
        refetchSessions();
      },
      onError: (error) => {
        toast.error(error.message || t('sessionSettings.revokeOtherErrorToast'));
      },
      onSettled: () => {
        setIsRevokingOtherSessions(false);
      },
    });

  const handleRevokeSession = (sessionToken: string) => {
    setIsRevokingSession(sessionToken);
    revokeSessionMutation({ sessionToken });
  };

  const handleRevokeOtherSessions = () => {
    setIsRevokingOtherSessions(true);
    revokeOtherSessionsMutation();
  };

  return (
    <div className="space-y-4 p-1">
      {isLoadingSessions ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : sessions.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t('sessionSettings.noSessions')}
        </p>
      ) : (
        <ul className="space-y-2">
          {sessions.map((session) => {
            const displayAgent = session.userAgent || t('sessionSettings.unknownDevice');
            return (
              <li
                key={session.token}
                className="flex items-center justify-between p-3" // Removed border and rounded
              >
                <div className="text-sm space-y-1 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="break-all flex-1">
                      {displayAgent}
                    </p>
                    {session.isCurrent && <Badge variant="outline">{t('sessionSettings.currentBadge')}</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {session.ipAddress ? `${t('sessionSettings.ipPrefix')}${session.ipAddress} | ` : ""}
                    {t('sessionSettings.lastUsedPrefix')}{new Date(session.updatedAt).toLocaleString()}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRevokeSession(session.token)}
                  disabled={isRevokingSession === session.token || session.isCurrent}
                  aria-label={`${t('sessionSettings.revokeAriaLabelPrefix')}${displayAgent}${session.isCurrent ? t('sessionSettings.revokeAriaLabelSuffixCurrent') : ""}`}
                  className="ml-2 flex-shrink-0"
                >
                {isRevokingSession === session.token ? (
                  <span className="loading loading-spinner loading-xs" />
                ) : (
                  <LogOut className={`h-4 w-4 ${session.isCurrent ? 'text-muted-foreground' : 'text-destructive'}`} />
                )}
              </Button>
              </li>
            );
          })}
        </ul>
      )}
      {sessions.length > 1 && (
        <Button
          variant="outline"
          onClick={handleRevokeOtherSessions}
          disabled={isRevokingOtherSessions || isLoadingSessions}
          className="mt-4"
        >
          {isRevokingOtherSessions
            ? t('sessionSettings.revokingOtherButton')
            : t('sessionSettings.revokeOtherButton')}
        </Button>
      )}
    </div>
  );
}
